// Reads any supported file into the shared ImportPreview
// (spec/fixtures/import/README.md). The format comes from the file's content,
// never its name or MIME type, so `.gpx.xml`, no extension and
// application/octet-stream all work.

import FitParser from 'fit-file-parser';
import { inflateSync, strFromU8 } from 'fflate';
import { analyse, emptyPreview } from './importRules';
import { csvText, isCsv, peaksCsvPreview } from './importPeaksCsv';
import type { ImportFormat, ImportPoint, ImportPreview, RawFile, RawLine, RawWaypoint } from './importTypes';

export class NotReadable extends Error {}

const startsWith = (b: Uint8Array, sig: number[], at = 0): boolean => sig.every((v, i) => b[at + i] === v);

export function sniff(bytes: Uint8Array): ImportFormat | 'zip' | 'json' | null {
  if (bytes.length >= 12 && startsWith(bytes, [0x2e, 0x46, 0x49, 0x54], 8)) return 'fit'; // ".FIT"
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) return 'zip';
  const head = new TextDecoder('utf-8').decode(bytes.subarray(0, 4096)).replace(/^﻿/, '');
  const first = head.trimStart()[0];
  if (first === '{' || first === '[') return 'json';
  if (/<gpx[\s>]/i.test(head)) return 'gpx';
  if (/<kml[\s>]/i.test(head)) return 'kml';
  if (/<TrainingCenterDatabase[\s>]/.test(head)) return 'tcx';
  // Last, so no route file is ever taken for one: a header naming a peak, or any table.
  if (isCsv(head)) return 'csv';
  return null;
}

// ---------------------------------------------------------------- XML helpers
function parseXml(text: string): Document {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new NotReadable('xml');
  return doc;
}
const all = (el: Document | Element, name: string): Element[] => Array.from(el.getElementsByTagNameNS('*', name));
const child = (el: Element, name: string): Element | undefined =>
  Array.from(el.children).find(c => c.localName === name);
const text = (el: Element | undefined): string => (el?.textContent ?? '').trim();
const num = (s: string | null | undefined): number | null => {
  if (s == null || s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const time = (s: string): number | null => {
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
};

// ---------------------------------------------------------------- GPX
function readGpx(doc: Document): RawFile {
  const pt = (el: Element): ImportPoint | null => {
    const lat = num(el.getAttribute('lat'));
    const lon = num(el.getAttribute('lon'));
    if (lat == null || lon == null) return null;
    return { lat, lon, ele: num(text(child(el, 'ele'))), time: time(text(child(el, 'time'))) };
  };
  const ok = (p: ImportPoint | null): p is ImportPoint => p !== null;
  const lines: RawLine[] = [];
  // Document order: tracks and routes keep the order they're written in.
  all(doc, '*').forEach(el => {
    if (el.localName === 'trk') {
      const segments = all(el, 'trkseg').map(seg => all(seg, 'trkpt').map(pt).filter(ok)).filter(s => s.length);
      if (segments.length) lines.push({ kind: 'track', name: text(child(el, 'name')) || null, segments });
    } else if (el.localName === 'rte') {
      const points = all(el, 'rtept').map(pt).filter(ok);
      if (points.length) lines.push({ kind: 'route', name: text(child(el, 'name')) || null, segments: [points] });
    }
  });
  const waypoints: RawWaypoint[] = all(doc, 'wpt')
    .filter(el => el.parentElement?.localName === 'gpx')
    .map(el => ({ el, p: pt(el) }))
    .filter((x): x is { el: Element; p: ImportPoint } => x.p !== null)
    .map(({ el, p }) => ({ name: text(child(el, 'name')), point: p }));
  return { format: 'gpx', lines, waypoints };
}

// ---------------------------------------------------------------- KML / KMZ
const kmlCoords = (s: string): ImportPoint[] =>
  s.trim().split(/\s+/).map(tuple => tuple.split(',').map(Number)).filter(c => c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]))
    .map(c => ({ lon: c[0], lat: c[1], ele: Number.isFinite(c[2]) ? c[2] : null, time: null }));

function readKml(doc: Document, format: 'kml' | 'kmz'): RawFile {
  const lines: RawLine[] = [];
  const waypoints: RawWaypoint[] = [];
  all(doc, 'Placemark').forEach(pm => {
    const name = text(child(pm, 'name'));
    all(pm, 'LineString').forEach(ls => {
      const points = kmlCoords(text(all(ls, 'coordinates')[0]));
      if (points.length) lines.push({ kind: 'route', name: name || null, segments: [points] });
    });
    // gx:Track carries times, so it's a recorded track.
    all(pm, 'Track').forEach(tr => {
      const whens = all(tr, 'when').map(w => time(text(w)));
      const points = all(tr, 'coord').map((c, i) => {
        const [lon, lat, ele] = text(c).split(/\s+/).map(Number);
        return { lon, lat, ele: Number.isFinite(ele) ? ele : null, time: whens[i] ?? null };
      }).filter(p => Number.isFinite(p.lon) && Number.isFinite(p.lat));
      if (points.length) lines.push({ kind: 'track', name: name || null, segments: [points] });
    });
    all(pm, 'Point').forEach(p => {
      const [point] = kmlCoords(text(all(p, 'coordinates')[0]));
      if (point) waypoints.push({ name, point });
    });
  });
  return { format, lines, waypoints };
}

// Unzipping stops at this many bytes uncompressed in total (README, "ZIP archives").
const MAX_UNZIPPED = 256 * 1024 * 1024;

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  size: number;
  localHeader: number;
}

// The ZIP's central directory: every entry with the sizes it declares, in ZIP order.
function zipDirectory(b: Uint8Array): ZipEntry[] {
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength);
  for (let eocd = b.length - 22; eocd >= Math.max(0, b.length - 22 - 0xffff); eocd--) {
    if (view.getUint32(eocd, true) !== 0x06054b50) continue;
    const count = view.getUint16(eocd + 10, true);
    let at = view.getUint32(eocd + 16, true);
    const entries: ZipEntry[] = [];
    for (let i = 0; i < count; i++) {
      if (at + 46 > b.length || view.getUint32(at, true) !== 0x02014b50) throw new NotReadable('zip directory');
      const nameLength = view.getUint16(at + 28, true);
      const entry = {
        name: new TextDecoder('utf-8').decode(b.subarray(at + 46, at + 46 + nameLength)),
        method: view.getUint16(at + 10, true),
        compressedSize: view.getUint32(at + 20, true),
        size: view.getUint32(at + 24, true),
        localHeader: view.getUint32(at + 42, true)
      };
      // ZIP64 sizes don't fit 32 bits; files that big are past the cap anyway.
      if (entry.size === 0xffffffff || entry.compressedSize === 0xffffffff) throw new NotReadable('zip64');
      entries.push(entry);
      at += 46 + nameLength + view.getUint16(at + 30, true) + view.getUint16(at + 32, true);
    }
    return entries;
  }
  throw new NotReadable('zip');
}

// One entry's bytes. Inflating into a buffer one byte bigger than the declared size keeps
// memory bounded even when the header lies, and the filled extra byte gives the lie away.
function zipEntryData(b: Uint8Array, entry: ZipEntry): Uint8Array {
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const at = entry.localHeader;
  if (at + 30 > b.length || view.getUint32(at, true) !== 0x04034b50) throw new NotReadable('zip entry');
  const start = at + 30 + view.getUint16(at + 26, true) + view.getUint16(at + 28, true);
  const packed = b.subarray(start, start + entry.compressedSize);
  if (packed.length !== entry.compressedSize) throw new NotReadable('zip entry');
  if (entry.method === 0) {
    if (packed.length !== entry.size) throw new NotReadable('zip entry');
    return packed;
  }
  if (entry.method !== 8) throw new NotReadable('zip method');
  let data: Uint8Array;
  try {
    data = inflateSync(packed, new Uint8Array(entry.size + 1));
  } catch {
    throw new NotReadable('zip inflate');
  }
  if (data.length !== entry.size) throw new NotReadable('zip size');
  return data;
}

// Every entry of a ZIP, in ZIP order. Past MAX_UNZIPPED in total the whole ZIP is unreadable.
function unzipAll(bytes: Uint8Array): Record<string, Uint8Array> {
  const entries = zipDirectory(bytes);
  if (entries.reduce((sum, e) => sum + e.size, 0) > MAX_UNZIPPED) throw new NotReadable('zip too big');
  const files: Record<string, Uint8Array> = {};
  entries.forEach(entry => {
    files[entry.name] = entry.name.endsWith('/') ? new Uint8Array(0) : zipEntryData(bytes, entry);
  });
  return files;
}

const baseName = (path: string): string => path.split('/').pop() ?? path;
const isKml = (path: string): boolean => /\.kml$/i.test(path);
const isDocKml = (path: string): boolean => /(^|\/)doc\.kml$/i.test(path);
// Directories, macOS resource forks and dotfiles are never route files.
const isClutter = (path: string): boolean =>
  path.endsWith('/') || /(^|\/)__MACOSX\//.test(path) || baseName(path).startsWith('.');

// A KMZ's document: doc.kml if there is one, else the first .kml.
function readKmz(files: Record<string, Uint8Array>): RawFile {
  const names = Object.keys(files).filter(isKml);
  const name = names.find(isDocKml) ?? names[0];
  if (!name) throw new UnsupportedZip();
  return readKml(parseXml(strFromU8(files[name])), 'kmz');
}
class UnsupportedZip extends Error {}

// ---------------------------------------------------------------- GeoJSON
function readGeoJson(json: unknown): RawFile {
  const lines: RawLine[] = [];
  const waypoints: RawWaypoint[] = [];
  const p = (c: number[]): ImportPoint => ({ lon: c[0], lat: c[1], ele: c.length > 2 ? c[2] : null, time: null });
  const valid = (c: unknown): c is number[] => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]);
  const geometry = (g: any, name: string): void => {
    if (!g) return;
    if (g.type === 'LineString' && Array.isArray(g.coordinates)) {
      const pts = g.coordinates.filter(valid).map(p);
      if (pts.length) lines.push({ kind: 'route', name: name || null, segments: [pts] });
    } else if (g.type === 'MultiLineString' && Array.isArray(g.coordinates)) {
      g.coordinates.forEach((part: unknown[]) => geometry({ type: 'LineString', coordinates: part }, name));
    } else if (g.type === 'Point' && valid(g.coordinates)) {
      waypoints.push({ name, point: p(g.coordinates) });
    } else if (g.type === 'MultiPoint' && Array.isArray(g.coordinates)) {
      g.coordinates.filter(valid).forEach((c: number[]) => waypoints.push({ name, point: p(c) }));
    } else if (g.type === 'GeometryCollection' && Array.isArray(g.geometries)) {
      g.geometries.forEach((x: unknown) => geometry(x, name));
    }
  };
  const feature = (f: any): void => geometry(f?.geometry, typeof f?.properties?.name === 'string' ? f.properties.name : '');
  const root = json as any;
  if (root?.type === 'FeatureCollection' && Array.isArray(root.features)) root.features.forEach(feature);
  else if (root?.type === 'Feature') feature(root);
  else geometry(root, '');
  return { format: 'geojson', lines, waypoints };
}

const isGeoJson = (j: any): boolean =>
  !!j && typeof j === 'object' && ['FeatureCollection', 'Feature', 'Point', 'MultiPoint', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon', 'GeometryCollection'].includes(j.type);

// ---------------------------------------------------------------- TCX
// One Activity is one track; each Lap's Track is one of its segments.
function readTcx(doc: Document): RawFile {
  const point = (tp: Element): ImportPoint | null => {
    const pos = child(tp, 'Position');
    const lat = pos ? num(text(child(pos, 'LatitudeDegrees'))) : null;
    const lon = pos ? num(text(child(pos, 'LongitudeDegrees'))) : null;
    if (lat == null || lon == null) return null;
    return { lat, lon, ele: num(text(child(tp, 'AltitudeMeters'))), time: time(text(child(tp, 'Time'))) };
  };
  const segment = (track: Element): ImportPoint[] =>
    all(track, 'Trackpoint').map(point).filter((x): x is ImportPoint => x !== null);
  const containers = all(doc, 'Activity');
  const groups = containers.length ? containers.map(a => all(a, 'Track')) : [all(doc, 'Track')];
  const lines: RawLine[] = groups
    .map(tracks => tracks.map(segment).filter(s => s.length))
    .filter(segments => segments.length)
    .map(segments => ({ kind: 'track' as const, name: null, segments }));
  return { format: 'tcx', lines, waypoints: [] };
}

// ---------------------------------------------------------------- FIT
function readFit(bytes: Uint8Array): Promise<RawFile> {
  return new Promise((resolve, reject) => {
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
    new FitParser({ force: true, speedUnit: 'm/s', lengthUnit: 'm', elapsedRecordField: false }).parse(buffer, (error: unknown, data: any) => {
      if (error || !data) return reject(new NotReadable('fit'));
      const points: ImportPoint[] = (data.records ?? [])
        .filter((r: any) => r.position_lat != null && r.position_long != null)
        .map((r: any) => ({
          lat: r.position_lat,
          lon: r.position_long,
          ele: r.enhanced_altitude ?? r.altitude ?? null,
          time: r.timestamp ? new Date(r.timestamp).getTime() : null
        }));
      resolve({ format: 'fit', lines: points.length ? [{ kind: 'track', name: null, segments: [points] }] : [], waypoints: [] });
    });
  });
}

// ---------------------------------------------------------------- entry point
export async function readImportFile(bytes: Uint8Array): Promise<ImportPreview> {
  const kind = sniff(bytes);
  if (kind === null) return emptyPreview(null, 'unsupported_format');
  if (kind === 'csv') return peaksCsvPreview(csvText(bytes));
  const fmt: ImportFormat | null = kind === 'zip' ? 'kmz' : kind === 'json' ? 'geojson' : kind;
  try {
    let raw: RawFile;
    if (kind === 'fit') raw = await readFit(bytes);
    else if (kind === 'zip') raw = readKmz(unzipAll(bytes));
    else if (kind === 'json') {
      let json: unknown;
      try {
        json = JSON.parse(new TextDecoder('utf-8').decode(bytes).replace(/^﻿/, ''));
      } catch {
        throw new NotReadable('json');
      }
      if (!isGeoJson(json)) return emptyPreview(null, 'unsupported_format');
      raw = readGeoJson(json);
    } else {
      const doc = parseXml(new TextDecoder('utf-8').decode(bytes));
      raw = kind === 'gpx' ? readGpx(doc) : kind === 'kml' ? readKml(doc, 'kml') : readTcx(doc);
    }
    return analyse(raw);
  } catch (e) {
    if (e instanceof UnsupportedZip) return emptyPreview(null, 'unsupported_format');
    return emptyPreview(fmt, 'not_readable');
  }
}

export const readImportBlob = async (file: Blob): Promise<ImportPreview> =>
  readImportFile(new Uint8Array(await file.arrayBuffer()));

// ---------------------------------------------------------------- ZIP archives
export interface ImportFileResult {
  name: string;
  preview: ImportPreview;
  /** One of the route files a ZIP archive was unpacked into, rather than the file that was added. */
  fromArchive?: boolean;
}

// Whether a ZIP entry is a route file of its own (README, "ZIP archives").
function isRouteEntry(data: Uint8Array): boolean {
  const kind = sniff(data);
  if (kind === 'zip') {
    try {
      return Object.keys(unzipAll(data)).some(isKml);
    } catch {
      return false;
    }
  }
  if (kind === 'json') {
    try {
      return isGeoJson(JSON.parse(new TextDecoder('utf-8').decode(data).replace(/^\uFEFF/, '')));
    } catch {
      return false;
    }
  }
  // A bagged-peaks CSV is text like any note, not a route file.
  return kind !== null && kind !== 'csv';
}

// Reads what the user picked: one file, or — for a ZIP that isn't a KMZ — one file per
// route file inside it, in ZIP order and named by its base name.
export async function readImportFiles(bytes: Uint8Array, name: string): Promise<ImportFileResult[]> {
  if (sniff(bytes) !== 'zip') return [{ name, preview: await readImportFile(bytes) }];
  let files: Record<string, Uint8Array>;
  try {
    files = unzipAll(bytes);
  } catch {
    return [{ name, preview: emptyPreview('kmz', 'not_readable') }];
  }
  const entries = Object.entries(files).filter(([path]) => !isClutter(path));
  const routes = entries.filter(([, data]) => isRouteEntry(data));
  const kmz = entries.some(([path]) => isDocKml(path)) || (routes.length === 1 && sniff(routes[0][1]) === 'kml');
  if (kmz) return [{ name, preview: await readImportFile(bytes) }];
  if (!routes.length) return [{ name, preview: emptyPreview(null, 'unsupported_format') }];
  return Promise.all(routes.map(async ([path, data]) => ({ name: baseName(path), preview: await readImportFile(data), fromArchive: true })));
}
